import React from 'react';
import { ConfigProvider, Typography } from 'antd';

const { Title, Text } = Typography;

/**
 * The header of a "Mis X" management page: a short text block on the left and
 * the page's primary action(s) on the right, wrapping onto two lines when the
 * column gets narrow.
 *
 * Every one of these pages (machines, services, accessories, operators,
 * transport vehicles) had its own hand-rolled copy of the same flex row, and
 * they had drifted -- different gaps, different margins, some with a title and
 * some without, some with a pill button and some with a square one. This is
 * the one shape they all share.
 *
 * Actions are rendered inside a scoped ConfigProvider that rounds buttons into
 * pills, so a call site can't accidentally reintroduce a square CTA by
 * forgetting `shape="round"`.
 */
export interface IPageHeaderRowProps {
  /** Optional -- several pages deliberately show only the subtitle, since the nav already names the section. */
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Right-hand action(s). Pass a fragment for more than one; they share a 12px gap. */
  actions?: React.ReactNode;
}

const PageHeaderRow: React.FC<IPageHeaderRowProps> = ({ title, subtitle, actions }) => {
  if (!title && !subtitle && !actions) return null;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: 12,
        marginBottom: 8,
      }}
    >
      <div>
        {title && (
          <Title level={4} style={{ marginBottom: 4 }}>
            {title}
          </Title>
        )}
        {subtitle && <Text type="secondary">{subtitle}</Text>}
      </div>
      {actions && (
        <ConfigProvider theme={{ components: { Button: { borderRadius: 999, borderRadiusLG: 999, borderRadiusSM: 999 } } }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginLeft: 'auto' }}>{actions}</div>
        </ConfigProvider>
      )}
    </div>
  );
};

export default PageHeaderRow;
